import DatePicker, { DatePickerProps, RangePickerProps } from 'antd/es/date-picker';
import classNames from 'classnames';
import { ReactNode, useCallback, useState } from 'react';
import { formatValidationError } from '../utils/validation-error.ts';
import './CalendarInputStyle.css';

const acceptedFormats = [
  'DD-MM-YYYY',
  'DD/MM/YYYY',
  'DD.MM.YYYY',
  'D-M-YYYY',
  'D-M-YY',
  'D/M/YYYY',
  'D/M/YY',
  'D.M.YYYY',
  'D.M.YY',
  'HH:mm',
];

export interface CalendarInputProps extends DatePickerProps {
  label?: string;
  labelButton?: ReactNode | JSX.Element;
  error?: string;
  range?: boolean;
  rangeOnChange?: RangePickerProps['onChange'];
  rangeDefaultValue?: RangePickerProps['defaultValue'];
  rangeValue?: RangePickerProps['value'];
  rangeOnOk?: RangePickerProps['onOk'];
  rangePlaceholder?: RangePickerProps['placeholder'];
  rangeOnCalendarChange?: RangePickerProps['onCalendarChange'];
  rangeOnPickerValueChange?: RangePickerProps['onPickerValueChange'];
  rangePresets?: RangePickerProps['presets'];
  rangeMode?: RangePickerProps['mode'];
  rangeOnPanelChange?: RangePickerProps['onPanelChange'];
  rangeShowTime?: RangePickerProps['showTime'];
  allowEmpty?: boolean | [boolean, boolean];
  classNamesPicker?: string;
  classNameLabel?: string;
  showErrorExample?: boolean;
}

const { RangePicker } = DatePicker;

export const CalendarInput = ({
  onOk,
  error,
  label,
  value,
  mode,
  showTime,
  presets,
  onChange,
  className,
  rangeMode,
  rangeOnOk,
  rangeValue,
  allowEmpty,
  labelButton,
  defaultValue,
  placeholder,
  onPanelChange,
  classNameLabel,
  rangeOnChange,
  rangePresets,
  rangeShowTime,
  rangePlaceholder,
  onCalendarChange,
  classNamesPicker,
  rangeDefaultValue,
  rangeOnPanelChange,
  onPickerValueChange,
  rangeOnCalendarChange,
  rangeOnPickerValueChange,
  range = false,
  showErrorExample = true,
  picker = 'date',
  // format = 'DD-MM-YYYY',
  ...props
}: CalendarInputProps) => {
  const [open, setOpen] = useState(false);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
  };

  const handleClick = () => {
    setOpen(!open);
  };

  const handleOnKeyDown = useCallback(
    (e: any) => {
      if (e.key === 'Enter' && open) e.preventDefault();
    },
    [open],
  );

  return (
    <div className={classNames('relative w-full flex flex-col', className)}>
      {label && (
        <label
          htmlFor={props.id || props.name}
          className={classNames('text-xs text-[#475569] mb-2 leading-[18px]', classNameLabel)}
        >
          <span className="text-gray-900 text-[10px] leading-[18px] font-meduim opacity-64 uppercase mb-2">
            {label}
          </span>
          {labelButton}
        </label>
      )}
      {range ? (
        <RangePicker
          className={classNames(
            'rounded-none border-0 outline-none shadow-none border-gray-200 w-full px-0',
            'hover:border-gray-900',
            {
              'border-error-red': error,
              'py-0 pt-1.5 pb-3': !classNamesPicker?.includes('py-'),
              'px-0': !classNamesPicker?.includes('px-'),
              'border-b': !classNamesPicker?.includes('border-'),
            },
            classNamesPicker,
          )}
          onChange={rangeOnChange}
          defaultValue={rangeDefaultValue}
          mode={rangeMode}
          onCalendarChange={rangeOnCalendarChange}
          onOk={rangeOnOk}
          onPanelChange={rangeOnPanelChange}
          onPickerValueChange={rangeOnPickerValueChange}
          placeholder={rangePlaceholder}
          presets={rangePresets}
          showTime={rangeShowTime}
          value={rangeValue}
          open={open}
          onOpenChange={handleOpenChange}
          onClick={handleClick}
          format={acceptedFormats}
          allowEmpty={allowEmpty}
          id={props.id || props.name}
          onKeyDown={handleOnKeyDown}
          picker={picker}
          {...props}
        />
      ) : (
        <DatePicker
          className={classNames(
            'rounded-none border-0 outline-none shadow-none border-gray-200 w-full',
            'hover:border-gray-900 [&>div>input]:!placeholder-[#A3A3A3]',
            {
              'border-error-red': error,
              'py-0 pt-1.5 pb-3':
                !classNamesPicker?.includes('py-') &&
                !classNamesPicker?.includes('pt-') &&
                !classNamesPicker?.includes('pb-'),
              'px-0': !classNamesPicker?.includes('px-'),
              'border-b': !classNamesPicker?.includes('border-'),
            },
            classNamesPicker,
          )}
          format={acceptedFormats}
          onChange={onChange}
          defaultValue={defaultValue}
          value={value}
          open={open}
          onOpenChange={handleOpenChange}
          onClick={handleClick}
          onOk={onOk}
          placeholder={placeholder}
          onCalendarChange={onCalendarChange}
          onPickerValueChange={onPickerValueChange}
          presets={presets}
          mode={mode}
          onPanelChange={onPanelChange}
          showTime={showTime}
          id={props.id || props.name}
          onKeyDown={handleOnKeyDown}
          picker={picker}
          {...props}
        />
      )}
      {error && (
        <p className="mt-2 text-sm text-red-500">
          {showErrorExample ? formatValidationError(error, '31/12/2026') : error}
        </p>
      )}
    </div>
  );
};
